import {
    ButtonGradientPrimary,
    DraggableList,
    Icon,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import QuestionEditForm from './QuestionEditForm';

import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { DraggableItem } from '@amelio/ui-library';

import {
    useGetCompanyValueQuestions,
    useDeleteCompanyValueQuestion,
    useToggleCompanyValueQuestionActive,
    useCreateCompanyValueQuestion,
    useUpdateCompanyValueQuestion,
    useReorderCompanyValueQuestions,
} from '@/api/company-value-questions';
import { userInstanceAtom } from '@/atoms/userDetails';
import { GripVerticalIcon } from '@/components/GripVerticalIcon/GripVerticalIcon';
import { useError } from '@/hooks/utils/useError';
import { questionSchema } from '@/pages/shared/QuestionnaireCreation/validation';
import { showWarningToast } from '@/plugins/Toast';
import { validationMessageKey } from '@/utils/validationMessage';

interface ValueQuestionsTabProps {
    companyValueId: string;
    companyValueName: string;
}

export default function ValueQuestionsTab({
    companyValueId,
    companyValueName,
}: ValueQuestionsTabProps) {
    const { t, i18n } = useTranslation();
    const user = useAtomValue(userInstanceAtom);
    const firstNamePlaceholder = user?.firstName ?? '';
    const { data: questions, isLoading, error } = useGetCompanyValueQuestions(companyValueId);
    const deleteMutation = useDeleteCompanyValueQuestion();
    const toggleActiveMutation = useToggleCompanyValueQuestionActive();
    const createMutation = useCreateCompanyValueQuestion();
    const updateMutation = useUpdateCompanyValueQuestion();
    const reorderMutation = useReorderCompanyValueQuestions();
    const { handleError } = useError();
    const [orderedQuestions, setOrderedQuestions] = useState<ReviewQuestion[]>([]);
    const [isAddingNew, setIsAddingNew] = useState(false);

    useEffect(() => {
        setOrderedQuestions(questions ?? []);
    }, [questions]);

    const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
    const safeCompanyValueName =
        companyValueName?.trim() || t('visionValues.fallbackQuestionDefaultValueName');
    const makeEmptyQuestion = (): ReviewQuestion => ({
        companyValueId,
        questionType: 'MultipleChoices',
        label: { [i18n.language]: '' },
        usefulTip: { [i18n.language]: '' },
        sortOrder: 0,
        isAnswerMandatory: false,
        isCommentAllowed: true,
        isIncludedInBestOrWorstQuestions: true,
        isIncludedInScoreCalculation: true,
        status: 'Active',
        reviewQuestionOptions: [],
    });
    const [newQuestion, setNewQuestion] = useState<ReviewQuestion>(makeEmptyQuestion);

    const handleDelete = (questionId: string) => {
        if (!questionId) {
            return;
        }

        deleteMutation.mutate({ id: questionId, companyValueId });
    };

    const handleToggleActive = (questionId: string) => {
        if (!questionId) {
            return;
        }

        toggleActiveMutation.mutate({ id: questionId, companyValueId });
    };

    const validateQuestion = (question: ReviewQuestion): boolean => {
        const result = questionSchema.safeParse(question);
        if (!result.success) {
            const errorMessage = validationMessageKey(result.error.issues[0]?.message);
            showWarningToast({ message: t(errorMessage) });
            return false;
        }
        return true;
    };

    const handleSave = (updatedQuestion: ReviewQuestion): boolean => {
        if (!validateQuestion(updatedQuestion)) return false;
        if (!updatedQuestion.id) {
            return false;
        }

        updateMutation.mutate({
            id: updatedQuestion.id,
            companyValueId,
            request: updatedQuestion,
        });
        setEditingQuestionId(null);

        return true;
    };

    const handleCreateQuestion = (updatedQuestion: ReviewQuestion): boolean => {
        if (!validateQuestion(updatedQuestion)) return false;
        const nextSortOrder =
            orderedQuestions.reduce((max, q) => Math.max(max, q.sortOrder ?? -1), -1) + 1;

        createMutation.mutate(
            { ...updatedQuestion, companyValueId, sortOrder: nextSortOrder },
            {
                onSuccess: () => {
                    setIsAddingNew(false);
                    setNewQuestion(makeEmptyQuestion());
                    setEditingQuestionId(null);
                },
            },
        );
        return true;
    };

    if (!companyValueId) {
        return (
            <div className={'flex flex-col items-center justify-center py-12 text-gray-500'}>
                <Icon
                    name={'essential-info-circle'}
                    size={'lg'}
                    colorClassName={'text-gray-400'}
                />

                <p className={'mt-4'}>{t('visionValues.saveValueBeforeAddingQuestions')}</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className={'flex items-center justify-center py-12'}>
                <div className={'text-gray-500'}>{t('visionValues.loading')}</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={'flex flex-col items-center justify-center py-12 text-red-500'}>
                <Icon
                    name={'essential-danger'}
                    size={'lg'}
                    colorClassName={'text-red-400'}
                />

                <p className={'mt-4'}>{t('visionValues.errorLoadingQuestions')}</p>
            </div>
        );
    }

    const hasActiveQuestions = orderedQuestions.some((q) => q.status === 'Active');

    const draggableQuestions: DraggableItem<ReviewQuestion>[] = orderedQuestions
        .filter((question): question is ReviewQuestion & { id: string } => Boolean(question.id))
        .map((question) => ({ id: question.id, data: question }));

    const isReorderDisabled =
        isAddingNew || editingQuestionId !== null || draggableQuestions.length < 2;

    const hasPendingWrite =
        updateMutation.isPending ||
        deleteMutation.isPending ||
        toggleActiveMutation.isPending ||
        reorderMutation.isPending;

    const handleReorder = (items: DraggableItem<ReviewQuestion>[]) => {
        if (hasPendingWrite) {
            return;
        }

        const reordered = items.map((item) => item.data);
        setOrderedQuestions(reordered);

        reorderMutation.mutate(
            { companyValueId, questions: reordered },
            { onError: (error) => handleError({ error }) },
        );
    };

    return (
        <div className={'flex flex-col gap-5'}>
            <div className={'flex items-center justify-between'}>
                <div className={'flex flex-col'}>
                    <h3 className={'text-navy text-body font-semibold'}>
                        {t('visionValues.assessmentQuestions')}
                    </h3>
                    <p className={'text-body-sm text-gray-500'}>
                        {t('visionValues.assessmentQuestionsDesc')}
                    </p>
                </div>

                <ButtonGradientPrimary
                    onClick={() => {
                        setIsAddingNew(true);
                        setEditingQuestionId(null);
                    }}
                    size={'sm'}
                >
                    {t('visionValues.addQuestion')}
                </ButtonGradientPrimary>
            </div>

            {!hasActiveQuestions && !isAddingNew && (
                <div
                    className={
                        'mb-4 flex items-start gap-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4'
                    }
                >
                    <svg
                        className={'mt-0.5 h-5 w-5 flex-shrink-0 text-[#64748B]'}
                        viewBox={'0 0 24 24'}
                        fill={'none'}
                        stroke={'currentColor'}
                        strokeWidth={1.5}
                        strokeLinecap={'round'}
                        strokeLinejoin={'round'}
                    >
                        <path d={'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'} />
                    </svg>

                    <p className={'text-body-sm leading-relaxed text-[#475467]'}>
                        <Trans
                            i18nKey={'visionValues.standardQuestionNotice'}
                            values={{
                                firstNamePlaceholder,
                                name: safeCompanyValueName,
                            }}
                            components={{
                                bold: <strong className={'font-semibold text-[#1E293B]'} />,
                            }}
                        />
                    </p>

                    <div className={'top-5 right-5'}>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type={'button'}
                                        className={
                                            'text-[#5E5E5E]/60 transition-colors hover:text-[#5E5E5E]'
                                        }
                                    >
                                        <svg
                                            className={'h-5 w-5'}
                                            viewBox={'0 0 24 24'}
                                            fill={'none'}
                                            stroke={'currentColor'}
                                            strokeWidth={2}
                                            strokeLinecap={'round'}
                                            strokeLinejoin={'round'}
                                        >
                                            <circle
                                                cx={'12'}
                                                cy={'12'}
                                                r={'10'}
                                            />
                                            <path d={'M12 16v-4'} />
                                            <path d={'M12 8h.01'} />
                                        </svg>
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent
                                    className={'max-w-xs'}
                                    side={'top'}
                                >
                                    <p>{t('visionValues.fallbackQuestionInfo')}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                </div>
            )}

            <div className={'flex flex-col gap-4'}>
                {isAddingNew && (
                    <QuestionEditForm
                        question={newQuestion}
                        onSave={handleCreateQuestion}
                        onDelete={() => {
                            setIsAddingNew(false);
                            setEditingQuestionId(null);
                        }}
                        onToggleActive={() => {}}
                        isFrozen={false}
                        isEditing={true}
                        onEditStart={() => {}}
                        onEditCancel={() => {
                            setIsAddingNew(false);
                            setEditingQuestionId(null);
                        }}
                    />
                )}

                <DraggableList
                    items={draggableQuestions}
                    onReorder={handleReorder}
                    disabled={isReorderDisabled}
                    gap={16}
                    ariaLabel={t('visionValues.assessmentQuestions')}
                    dataTestId={`value-questions-${companyValueId}`}
                    renderItem={(item) => (
                        <div
                            className={'flex w-full items-center gap-3'}
                            data-testid={`value-question-row-${item.id}`}
                        >
                            {!isReorderDisabled && (
                                <span
                                    className={'text-base-300 shrink-0 cursor-move'}
                                    data-testid={'drag-handle'}
                                >
                                    <GripVerticalIcon className={'h-6 w-6'} />
                                </span>
                            )}

                            <div className={'min-w-0 flex-1'}>
                                <QuestionEditForm
                                    question={item.data}
                                    onSave={handleSave}
                                    onDelete={() => handleDelete(item.id)}
                                    onToggleActive={() => handleToggleActive(item.id)}
                                    isFrozen={item.data.isFrozen ?? false}
                                    isPendingUpdate={updateMutation.isPending}
                                    isEditing={editingQuestionId === item.id}
                                    onEditStart={() => setEditingQuestionId(item.id)}
                                    onEditCancel={() => setEditingQuestionId(null)}
                                />
                            </div>
                        </div>
                    )}
                />
            </div>
        </div>
    );
}
